import { useCallback } from "react";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  Timestamp,
} from 'firebase/firestore';
import { FirestoreDB } from '../../../../utils/firebase/firebase';
import { addActivityLog } from '../../../../api/documentsAndGoals';
import { editStudentGoalEntry } from '../../../../api/students';
import { ACM } from '../../../../constants/UserRoles';
import { trackDataPointChanges } from '../utils/dataUtils';
import { isPreRegistered } from '../utils/userUtils';
import { safeJsonBtoa } from '../../../../utils/encodingUtils';

export const useDataSubmission = ({
  user,
  typeOfUser,
  selectedStudents,
  studentId,
  studentDataPoints,
  selectedDate,
  selectedGoalIds,
  goalTitle,
  goalNo,
  dataPoints,
  videoId,
  goalDate,
  allGoalData,
  queryClient,
  onClose,
  preRegisteredUsers = [],
  setIsDisabled,
  setIsSaving,
  setIsDeleting,
  onSuccess,
  editingDataPoint,
  fromSessionNotes = false,
  dataEntryRef = null,
  entryKey = null,
}) => {
  const showToastMessage = useCallback((status) => {
    if (fromSessionNotes) return;
    if (status === "success") {
      toast.success("Data entry uploaded successfully!", {
        position: toast.POSITION.BOTTOM_RIGHT,
        enableHtml: false,
      });
    } else if (status === "delete") {
      toast.success("Data entry deleted successfully!", {
        position: toast.POSITION.BOTTOM_RIGHT,
        enableHtml: false,
      });
    } else if (status === "edit") {
      toast.success("Data entry updated successfully!", {
        position: toast.POSITION.BOTTOM_RIGHT,
        enableHtml: false,
      });
    } else if (status === "delete-error") {
      toast.error("Data entry deletion failed!", {
        position: toast.POSITION.BOTTOM_RIGHT,
        enableHtml: false,
      });
    } else {
      toast.error("Data entry upload failed!", {
        position: toast.POSITION.BOTTOM_RIGHT,
        enableHtml: false,
      });
    }
  }, []);

  
  const handleSubmit = async (goalInput) => {
    let isSuccess = false;
    // Detect if this is a batch of multiple goals or a single goal submission
    const isMultiple =
      goalInput?.allGoalEntries && Array.isArray(goalInput.allGoalEntries);

    // Handle both single and multiple uniformly
    const goalEntries = isMultiple ? goalInput.allGoalEntries : [goalInput];
    setIsSaving(true);
    setIsDisabled(true);
    const studentID = isMultiple
      ? goalInput?.studentId
      : selectedStudents?.id || studentId;
    console.log(studentID, "studentID");
    const collectionName = isPreRegistered(studentID, preRegisteredUsers)
      ? "preRegisteredUsers"
      : ACM[typeOfUser]?.isDoctor || ACM[typeOfUser]?.isPatient
        ? "patientsList"
        : "studentsList";

    const currentTime = new Date();
    // const firestoreTimestamp = Timestamp.fromDate(dayjs(goalDate).toDate());

    try {
      let savedCount = 0;
      // Loop through each goal entry
      for (const goalItem of goalEntries) {
        const goalId =
          goalItem?.goalId ||
          goalItem?.existingEntry?.info?.goalId ||
          goalInput?.goalId ||
          goalInput;
        const goalTitle = goalItem?.goalTitle || "";
        const goalNo = goalItem?.goalNumber || "";
        const itemDataPoints = isMultiple
          ? goalItem?.dataPoints || []
          : dataPoints;
        // Prefer the date coming from studentDataPoints, but in bulk mode
        // fall back to the explicit itemGoalDate provided by the modal
        const itemGoalDate = isMultiple
          ? goalItem?.studentDataPoints?.[0]?.date || goalItem?.itemGoalDate
          : goalDate || selectedDate;

        if (!itemDataPoints || itemDataPoints.length === 0) {
          console.warn(`No data points found for goal ${goalId}, skipping...`);
          continue;
        }

        const parsedDate = dayjs(itemGoalDate);
        if (!parsedDate.isValid()) {
          continue;
        }
        const jsDate = parsedDate.toDate();
        const firestoreTimestamp = Timestamp.fromDate(jsDate);
        // const isEditing = editingDataPoint !== null;
        let existingEntry = null;

        if (isMultiple) {
          // In multiple mode, existing entry is already provided in goalItem
          existingEntry = goalItem?.existingEntry || null;
          console.log(
            "Multiple mode - existingEntry from goalItem:",
            existingEntry
          );
        } else {
          // In single mode, search in studentDataPoints
          existingEntry = studentDataPoints?.find(
            (dp) => dp.goalId === goalId && dp.date === itemGoalDate
          );
          console.log(
            "Single mode - existingEntry from studentDataPoints:",
            existingEntry
          );
        }

        // Show warning if data already exists for this date but we're creating new entry
        if (existingEntry && !editingDataPoint) {
          console.warn(
            `Data already exists for goal ${goalId} on date ${selectedDate}. Creating new entry.`
          );
        }

        // Determine if we should edit or create new entry
        // For multiple entries, we need to check if we have a valid existing entry
        let isEditing = false;

        if (editingDataPoint) {
          // Single mode editing
          isEditing = true;
        } else if (existingEntry) {
          // Check if existingEntry has the right structure for editing
          if (isMultiple) {
            // In multiple mode, existingEntry should have an id property
            isEditing = !!existingEntry.id;
          } else {
            // In single mode, existingEntry should have the right structure
            isEditing = !!(existingEntry.id || existingEntry.dataPoints?.id);
          }
        }

        const goal = goalId || allGoalData?.id;

        if (isEditing) {
          // ====================== UPDATE EXISTING ENTRY ======================
          const formData = {
            goal: goalTitle,
            goalNumber: goalNo,
            goalId: goal,
            data_points: itemDataPoints
              ?.filter((dp) => dp !== null)
              ?.map((dataPoint) => {
                // Ensure selectedOptions is always an array to prevent indexOf errors
                const selectedOptions = Array.isArray(dataPoint?.selectedOptions)
                  ? dataPoint.selectedOptions
                  : [];
                // Ensure trialsNumber is always an array to prevent map errors
                const trialsNumber = Array.isArray(dataPoint?.trialsNumber)
                  ? dataPoint.trialsNumber.map(Number)
                  : [];

                return {
                  id: dataPoint?.id || doc(collection(FirestoreDB, "temp")).id,
                  goal_value: dataPoint?.value,
                  trials_value: dataPoint?.trailsValue,
                  trialsNumber: trialsNumber,
                  abc_data: selectedOptions.includes("Add ABC Data")
                    ? dataPoint?.abcData
                    : "",
                  phase_change: selectedOptions.includes(
                    "Add Phase Change Line"
                  )
                    ? dataPoint?.phaseChange
                    : "",
                  preceding_behavior: selectedOptions.includes(
                    "Add Preceding Behavior"
                  )
                    ? dataPoint?.precedingBehavior
                    : "",
                  event: selectedOptions.includes("Add Event Line")
                    ? dataPoint?.eventLine
                    : "",
                  notes: dataPoint?.notes || "",
                  timeOfCollection: selectedOptions.includes(
                    "Add Time of Collection"
                  )
                    ? dataPoint?.timeOfCollection
                    : "",
                  timeOfCollectionSpecific: selectedOptions.includes(
                    "Add Time of Collection"
                  )
                    ? dataPoint?.timeOfCollectionSpecific || ""
                    : "",
                  promptingLevel: selectedOptions.includes(
                    "Add Prompting Level"
                  )
                    ? dataPoint?.promptingLevel
                    : "",
                  physicalAssistanceLevel: dataPoint?.physicalAssistanceLevel || "",
                  sessionSummary: selectedOptions.includes(
                    "Add Session Summary"
                  )
                    ? dataPoint?.sessionSummary || ""
                    : "",
                  groupSize: selectedOptions.includes(
                    "Add Group Size"
                  )
                    ? dataPoint?.groupSize || ""
                    : "",
                  sessionLength: selectedOptions.includes(
                    "Add Session Length"
                  )
                    ? dataPoint?.sessionLength || ""
                    : "",
                  cptCodes: selectedOptions.includes("Add CPT Code")
                    ? dataPoint?.cptCodes || ""
                    : "",
                  prompting: dataPoint?.prompting,
                  criteria: dataPoint?.criteria,
                  currentGoal: dataPoint?.currentGoal,
                  videoId,
                  currentTime: currentTime.toISOString(),
                  prompting_measurement: dataPoint?.value || "",
                  taskAnalysis: dataPoint?.taskAnalysis || {},
                  additionalStaff:
                    typeof dataPoint?.additionalStaff === "boolean"
                      ? dataPoint?.additionalStaff
                      : dataPoint?.additionalStaff === ""
                        ? ""
                        : false,
                  numberOfStaffs:
                    typeof dataPoint?.numberOfStaffs === "number"
                      ? dataPoint?.numberOfStaffs
                      : 0,
                  interventions: dataPoint?.interventions || [],
                  customData: (() => {
                    if (
                      dataPoint?.customSection &&
                      Array.isArray(dataPoint.customSection)
                    ) {
                      return dataPoint.customSection.map((section) => ({
                        docId: section.docId,
                        definition: section.definition || '',
                        title: section.title || '',
                        questions: section.questions || [],
                      }));
                    }
                    return [];
                  })(),
                };
              }),
          };

          const entryToUpdate = editingDataPoint || existingEntry;
          let dataCollectionId, goalDataId;

          // Add null check for entryToUpdate to prevent the error
          if (!entryToUpdate) {
            console.error(
              "entryToUpdate is null or undefined, skipping update for goal:",
              goalId
            );

            // Instead of skipping, let's switch to create mode
            // We'll fall through to the else block below
          } else {
            if (isEditing) {
              dataCollectionId = goal;
              goalDataId = entryToUpdate.id;
            } else {
              dataCollectionId =
                entryToUpdate.dataCollectionId || entryToUpdate.id;
              goalDataId = entryToUpdate.dataPoints?.id || entryToUpdate.id;
            }

            // console.log("Updating data entry:", {
            //   isEditing,
            //   dataCollectionId,
            //   goalDataId,
            // });

            await editStudentGoalEntry(
              user?.uid,
              typeOfUser,
              studentID,
              dataCollectionId,
              goalDataId,
              safeJsonBtoa(formData)
            );



            if (fromSessionNotes && dataEntryRef) {
              dataEntryRef.current = dataEntryRef.current || {};

              const captureKey = goalId || goal;

              dataEntryRef.current[captureKey] = goalDataId;
            }

            const changes = trackDataPointChanges(entryToUpdate, formData);
            const sanitizedChanges = JSON.parse(JSON.stringify(changes));

            const activityEntry = {
              actionPerformed: sanitizedChanges,
              editedBy: user?.uid ?? null,
              typeOfActivity: "Edited Data Entry",
              createdAt: Timestamp.now(),
            };

            const encodedActivityData = safeJsonBtoa(activityEntry);

            await addActivityLog(
              collectionName,
              studentID,
              "dataCollection",
              goal,
              encodedActivityData
            );
            savedCount += 1;
          }
        } else {
          // ====================== CREATE NEW ENTRY ======================
          const formData = {
            goal: goalTitle,
            goalNumber: goalNo,
            data_points: (itemDataPoints || []).map((dp) => ({
              id: dp?.id || doc(collection(FirestoreDB, "temp")).id,
              goal_value: dp.value,
              trialsNumber: dp.trialsNumber.map(Number),
              trials_range: dp.trailsValue,
              abc_data: dp.selectedOptions.includes("Add ABC Data")
                ? dp.abcData
                : "",
              phase_change: dp.selectedOptions.includes("Add Phase Change Line")
                ? dp.phaseChange
                : "",
              preceding_behavior: dp.selectedOptions.includes(
                "Add Preceding Behavior"
              )
                ? dp.precedingBehavior
                : "",
              event: dp.selectedOptions.includes("Add Event Line")
                ? dp.eventLine
                : "",
              notes: dp.notes,
              timeOfCollection: dp.selectedOptions.includes(
                "Add Time of Collection"
              )
                ? dp.timeOfCollection
                : "",
              timeOfCollectionSpecific: dp.selectedOptions.includes(
                "Add Time of Collection"
              )
                ? dp.timeOfCollectionSpecific || ""
                : "",
              promptingLevel: dp.selectedOptions.includes("Add Prompting Level")
                ? dp.promptingLevel
                : "",
              physicalAssistanceLevel: dp.physicalAssistanceLevel || "",
              sessionSummary: dp.selectedOptions.includes("Add Session Summary")
                ? dp.sessionSummary || ""
                : "",
              groupSize: dp.selectedOptions.includes("Add Group Size")
                ? dp.groupSize === "Other" && dp.customGroupSize
                  ? `Group of ${dp.customGroupSize}`
                  : dp.groupSize || ""
                : "",
              customGroupSize: dp.selectedOptions.includes("Add Group Size")
                ? dp.customGroupSize || ""
                : "",
              sessionLength: dp.selectedOptions.includes("Add Session Length")
                ? dp.sessionLength || ""
                : "",
              cptCodes: dp.selectedOptions.includes("Add CPT Code")
                ? dp.cptCodes || ""
                : "",
              prompting: dp.prompting,
              criteria: dp.criteria,
              currentGoal: dp.currentGoal,
              videoId,
              currentTime: currentTime.toISOString(),
              prompting_measurement: dp?.value || "",
              taskAnalysis: dp?.taskAnalysis || {},
              additionalStaff:
                typeof dp?.additionalStaff === "boolean"
                  ? dp?.additionalStaff
                  : dp?.additionalStaff === ""
                    ? ""
                    : false,
              numberOfStaffs:
                typeof dp?.numberOfStaffs === "number" ? dp.numberOfStaffs : 0,
              interventions: dp?.interventions || [],
              customData: (() => {
                if (
                  dp?.customSection &&
                  Array.isArray(dp.customSection)
                ) {
                  return dp.customSection.map((section) => ({
                    docId: section.docId,
                    definition: section.definition || '',
                    title: section.title || '',
                    questions: section.questions || [],
                  }));
                }
                return [];
              })(),
            })),
          };
          const dataEntryDocRef = await addDoc(
            collection(
              FirestoreDB,
              collectionName,
              studentID,
              "dataCollection",
              goal,
              "goalData"
            ),
            {
              info: safeJsonBtoa(formData),
              createdAt: firestoreTimestamp,
              isEncrypted: false,
            }
          );

          // Capture dataEntryId when submitting from session notes
          if (fromSessionNotes && dataEntryRef && entryKey) {
            dataEntryRef.current = dataEntryRef.current || {};
            const captureKey = goalItem?.goalId || goal;
            dataEntryRef.current[captureKey] = dataEntryDocRef.id;
          }

          const activityEntry = {
            actionPerformed: `Added new data entry for ${dayjs(itemGoalDate).isValid() ? dayjs(itemGoalDate).format('MM-DD-YYYY') : itemGoalDate}`,
            editedBy: user?.uid,
            typeOfActivity: "New Data Entry",
            createdAt: Timestamp.now(),
          };

          const encodedActivityData = safeJsonBtoa(activityEntry);

          await addActivityLog(
            collectionName,
            studentID,
            "dataCollection",
            goal,
            encodedActivityData
          );
          savedCount += 1;
        }
      } // end loop

      // Refresh all related queries after all updates
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["fetchStudentData", user?.uid, typeOfUser],
          refetchType: "all",
        }),
        queryClient.invalidateQueries({
          queryKey: ["fetchStudentData", user?.uid, typeOfUser, true, true],
          refetchType: "all",
        }),
        queryClient.invalidateQueries({
          queryKey: ["fetchStudentData", user?.uid, typeOfUser, false, false],
          refetchType: "all",
        }),
      ]);

      if (onSuccess) {
        try {
          await onSuccess();
        } catch (callbackError) {
          console.error("Error in onSuccess callback:", callbackError);
        }
      }

      if (savedCount === 0) {
        throw new Error("No entries were saved in bulk operation");
      }

      if (isMultiple) {
        showToastMessage("success", "All goals saved successfully!");
      } else if (editingDataPoint || studentDataPoints?.length) {
        showToastMessage("edit");
      } else {
        showToastMessage("success");
      }
      isSuccess = true;
    } catch (error) {
      console.error("Error saving data:", error);
      showToastMessage("error");
    } finally {
      setIsDisabled(false);
      setIsSaving(false);
    }

    return isSuccess;
  };

  // Helper function to invalidate queries
  const invalidateQueries = async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: ["fetchStudentData", user?.uid, typeOfUser],
        refetchType: "all",
      }),
      queryClient.invalidateQueries({
        queryKey: ["fetchStudentData", user?.uid, typeOfUser, true, true],
        refetchType: "all",
      }),
      queryClient.invalidateQueries({
        queryKey: ["fetchStudentData", user?.uid, typeOfUser, false, false],
        refetchType: "all",
      }),
    ]);
  };

  const handleDelete = useCallback(
    async (goalId) => {
      setIsDeleting(true);
      const studentID = selectedStudents?.id || studentId;

      const collectionName = isPreRegistered(studentID, preRegisteredUsers)
        ? "preRegisteredUsers"
        : ACM[typeOfUser]?.isDoctor || ACM[typeOfUser]?.isPatient
          ? "patientsList"
          : "studentsList";

      const existingEntry = studentDataPoints?.find(
        (dp) => dp.date === selectedDate
      );

      try {
        if (existingEntry) {
          const dataCollectionId = existingEntry.dataPoints.id;
          const goalDocRef = doc(
            FirestoreDB,
            collectionName,
            studentID,
            "dataCollection",
            goalId,
            "goalData",
            dataCollectionId
          );

          const activityEntry = {
            actionPerformed: `Data entry from ${existingEntry?.date ||
              allGoalData?.DataCollection?.ByDate ||
              allGoalData?.dataCollection?.DataCollection?.ByDate ||
              "-"
              } deleted`,
            editedBy: user?.uid,
            typeOfActivity: "Delete Data Entry",
            createdAt: Timestamp.now(),
          };

          const encodedActivityData = safeJsonBtoa(activityEntry);

          await addActivityLog(
            collectionName,
            studentID,
            "dataCollection",
            goalId,
            encodedActivityData
          );

          await deleteDoc(goalDocRef);

          await Promise.all([
            queryClient.invalidateQueries({
              queryKey: [
                "fetchStudentData",
                user?.uid,
                typeOfUser,
                false,
                false,
              ],
              refetchType: "active",
            }),
            queryClient.invalidateQueries({
              queryKey: ["fetchStudentData", user?.uid, typeOfUser, true, true],
              refetchType: "active",
            }),
          ]);
          if (onSuccess) {
            await onSuccess();
          }

          showToastMessage("delete");

          if (typeof onClose === "function") {
            onClose();
          }
        }
      } catch (error) {
        console.error("Error deleting data:", error);
        showToastMessage("delete-error");
      } finally {
        setIsDeleting(false);
      }
    },
    [
      selectedStudents,
      studentId,
      studentDataPoints,
      selectedDate,
      allGoalData,
      user,
      typeOfUser,
      queryClient,
      onClose,
      showToastMessage,
      preRegisteredUsers,
      onSuccess,
      setIsDeleting,
    ]
  );

  return {
    handleSubmit,
    handleDelete,
    showToastMessage,
  };
};
